/**
 * LNV Wishlist Page
 * Version: 1.0
 *
 * Reads the existing browser-based LNV wishlist.
 * Storage key must match lnv-wishlist.js.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'lnv:wishlist:v1';

  const root = document.querySelector(
    '[data-lnv-wishlist-page]'
  );

  if (!root) return;

  const grid = root.querySelector(
    '[data-lnv-wishlist-grid]'
  );

  const emptyState = root.querySelector(
    '[data-lnv-wishlist-empty]'
  );

  const status = root.querySelector(
    '[data-lnv-wishlist-status]'
  );

  if (!grid || !emptyState || !status) return;


  /* =======================================================
     STORAGE
     ======================================================= */

  function readWishlist() {
    try {
      const raw =
        window.localStorage.getItem(STORAGE_KEY);

      if (!raw) return [];

      const parsed = JSON.parse(raw);

      return Array.isArray(parsed)
        ? parsed
        : [];
    } catch (error) {
      return [];
    }
  }


  function writeWishlist(items) {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(items)
      );
    } catch (error) {
      /* Keep page usable if storage is unavailable. */
    }
  }


  function normalizeItem(item) {
    if (!item || !item.id || !item.url) {
      return null;
    }

    return {
      id: String(item.id),

      title:
        typeof item.title === 'string' &&
        item.title.trim()
          ? item.title.trim()
          : 'Saved product',

      url: String(item.url),

      image:
        typeof item.image === 'string'
          ? item.image
          : '',

      price:
        typeof item.price === 'string'
          ? item.price
          : ''
    };
  }


  /* =======================================================
     ICON
     ======================================================= */

  function createHeartIcon() {
    const svg = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'svg'
    );

    svg.setAttribute(
      'viewBox',
      '0 0 24 24'
    );

    svg.setAttribute(
      'aria-hidden',
      'true'
    );

    svg.setAttribute(
      'focusable',
      'false'
    );

    const path = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path'
    );

    path.setAttribute(
      'd',
      'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z'
    );

    svg.appendChild(path);

    return svg;
  }


  /* =======================================================
     STATUS
     ======================================================= */

  function updateSummary(
    items,
    announcement
  ) {
    const count = items.length;

    if (announcement) {
      status.textContent = announcement;
      return;
    }

    status.textContent =
      count === 1
        ? '1 saved item'
        : count + ' saved items';
  }


  /* =======================================================
     REMOVE
     ======================================================= */

  function removeItem(
    productId,
    productTitle
  ) {
    const items = readWishlist()
      .map(normalizeItem)
      .filter(Boolean)
      .filter(function (item) {
        return (
          item.id !== String(productId)
        );
      });

    writeWishlist(items);

    render(items);

    updateSummary(
      items,
      productTitle +
        ' removed from your wishlist.'
    );

    window.dispatchEvent(
      new CustomEvent(
        'lnv:wishlist:changed',
        {
          detail: {
            count: items.length
          }
        }
      )
    );
  }


  /* =======================================================
     PRODUCT CARD
     ======================================================= */

  function createCard(item) {
    const article =
      document.createElement('article');

    article.className =
      'lnv-wishlist-page__card';

    article.dataset.productId =
      item.id;


    /* Image link */

    const mediaLink =
      document.createElement('a');

    mediaLink.className =
      'lnv-wishlist-page__media';

    mediaLink.href = item.url;

    mediaLink.setAttribute(
      'aria-label',
      'View ' + item.title
    );


    if (item.image) {
      const image =
        document.createElement('img');

      image.className =
        'lnv-wishlist-page__image';

      image.src = item.image;
      image.alt = item.title;

      image.loading = 'lazy';
      image.decoding = 'async';

      mediaLink.appendChild(image);
    } else {
      const placeholder =
        document.createElement('span');

      placeholder.className =
        'lnv-wishlist-page__image-placeholder';

      placeholder.setAttribute(
        'aria-hidden',
        'true'
      );

      mediaLink.appendChild(
        placeholder
      );
    }


    /* Remove button */

    const removeButton =
      document.createElement('button');

    removeButton.type = 'button';

    removeButton.className =
      'lnv-wishlist-page__remove';

    removeButton.setAttribute(
      'aria-label',
      'Remove ' +
        item.title +
        ' from wishlist'
    );

    removeButton.title =
      'Remove from wishlist';

    removeButton.appendChild(
      createHeartIcon()
    );

    removeButton.addEventListener(
      'click',
      function () {
        removeItem(
          item.id,
          item.title
        );
      }
    );


    /* Content */

    const content =
      document.createElement('div');

    content.className =
      'lnv-wishlist-page__card-content';


    const title =
      document.createElement('h2');

    title.className =
      'lnv-wishlist-page__card-title';


    const titleLink =
      document.createElement('a');

    titleLink.href = item.url;
    titleLink.textContent = item.title;

    title.appendChild(titleLink);

    content.appendChild(title);


    if (item.price) {
      const price =
        document.createElement('p');

      price.className =
        'lnv-wishlist-page__price';

      price.textContent = item.price;

      content.appendChild(price);
    }


    const viewLink =
      document.createElement('a');

    viewLink.className =
      'lnv-wishlist-page__view';

    viewLink.href = item.url;

    viewLink.textContent =
      'View product';

    content.appendChild(viewLink);


    article.appendChild(mediaLink);
    article.appendChild(removeButton);
    article.appendChild(content);

    return article;
  }


  /* =======================================================
     RENDER
     ======================================================= */

  function render(providedItems) {
    const items =
      (providedItems || readWishlist())
        .map(normalizeItem)
        .filter(Boolean);

    grid.replaceChildren();


    if (!items.length) {
      grid.hidden = true;
      emptyState.hidden = false;

      updateSummary(items);

      return;
    }


    emptyState.hidden = true;
    grid.hidden = false;


    const fragment =
      document.createDocumentFragment();


    items.forEach(function (item) {
      fragment.appendChild(
        createCard(item)
      );
    });


    grid.appendChild(fragment);

    updateSummary(items);
  }


  /* =======================================================
     INIT
     ======================================================= */

  render();


  /* Sync if wishlist changes in another browser tab */

  window.addEventListener(
    'storage',
    function (event) {
      if (event.key === STORAGE_KEY) {
        render();
      }
    }
  );

})();